import React, { createContext, useContext, useEffect, useState } from 'react';

import { AxiosError } from 'axios';

import IUser from '../interfaces/user.interface';
import api from '../services/api';
import { UserService, IUserSignInData } from '../services/auth.service';

enum StoragePrefix {
  user = 'ib-admin:user',
  token = 'ib-admin:token',
  tokenExp = 'ib-admin:token-exp',
  sessionPersistence = 'ib-admin:persistence',
}

type AuthContextData = {
  readonly user: IUser | undefined;
  signIn: (data: IUserSignInData) => Promise<{ message: string; type: string }>;
  setSessionPersistence: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => void;
  readonly authToken: string;
};

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const [sessionPersistence, setSessionPersistence] = useState<boolean>(() => {
    const session = !!localStorage.getItem(StoragePrefix.sessionPersistence);

    if (!session) {
      localStorage.setItem(StoragePrefix.sessionPersistence, `${false}`);
      return false;
    }
    return session;
  });
  const [authToken, setAuthToken] = useState<string>('');
  const [user, setUser] = useState(() => {
    const userFromStorage = localStorage.getItem(StoragePrefix.user);
    const token = localStorage.getItem(StoragePrefix.token);

    if (token && userFromStorage) {
      const user: IUser = JSON.parse(userFromStorage);

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      return user;
    }
    return undefined;
  });

  async function signIn({
    email,
    password,
  }: IUserSignInData): Promise<{ message: string; type: string }> {
    try {
      const { data } = await UserService.signIn({
        email,
        password,
      });

      /*
      if (data.user.role !== 'admin') {
        return {
          message: 'Você não tem permissão necessária para acessar o sistema!',
          type: 'error',
        };
      }
      */

      localStorage.setItem(StoragePrefix.user, JSON.stringify(data.user));
      localStorage.setItem(StoragePrefix.token, data.data.token);
      localStorage.setItem(StoragePrefix.tokenExp, data.data.expires_at);

      api.defaults.headers.common.Authorization = `Bearer ${data.data.token}`;
      setAuthToken(`Bearer ${localStorage.getItem(StoragePrefix.token)}`);

      setUser(data.user);
      return { message: 'Usuário logado com sucesso!', type: 'sucess' };
    } catch (error: AxiosError | any) {
      if (error.response) {
        if (error.response.status === 404) {
          return { message: 'Usuário não encontrado!', type: 'error' };
        }
        if (error.response.status === 400) {
          return { message: 'Email ou senha inválida!', type: 'error' };
        }
      }
      return {
        message: 'Não foi possível conectar ao servidor!',
        type: 'error',
      };
    }
  }

  function logout() {
    Object.values(StoragePrefix).forEach((item) => {
      localStorage.removeItem(item);
    });

    setUser(undefined);

    api.defaults.headers.common.Authorization = '';
  }

  useEffect(() => {
    if (!sessionPersistence) {
      window.onbeforeunload = () => {
        logout();
      };
    }
  }, [sessionPersistence]);

  useEffect(() => {
    const expires_at = localStorage.getItem(StoragePrefix.tokenExp);

    if (expires_at) {
      const now = new Date();
      const convertedDate: Date = new Date(expires_at);
      now >= convertedDate && logout();
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        setSessionPersistence,
        logout,
        authToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
